//
// Copyright (c) 2011-2024 by Curalate, Inc
//

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import { DefaultIconButton } from '@components/atoms/button/Button';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import { ContentDrawerTrackingIds } from '@content-drawer/contentDrawerConstants';
import { isVideo } from '@content-drawer/media/mediaUtils';

export interface PreviewMediaProps {
  item: IContentLibraryItem;
  onClose: () => void;
}

export const PreviewMedia = ({ item, onClose }: PreviewMediaProps) => {
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const isVideoType = isVideo(item);

  useEffect(() => {
    setIsImageLoaded(false);
    setIsVideoLoaded(false);
  }, [item]);

  return (
    <Wrapper>
      {!isImageLoaded && !isVideoLoaded && <CenteredSpinner />}
      {!isVideoLoaded && (
        <Image
          alt={item.description}
          crossOrigin='anonymous'
          isVisible={isImageLoaded}
          onLoad={() => setIsImageLoaded(true)}
          src={item.metadata.hiResSource}
        />
      )}
      {isVideoType && (
        <Video
          autoPlay={true}
          crossOrigin='anonymous'
          controls
          controlsList='nodownload noremoteplayback'
          isVisible={isVideoLoaded}
          onLoadedData={() => setIsVideoLoaded(true)}
        >
          {
            // TODO: PD-239796 - Investigate video player, Do we need to add muteStatus and caption
          }
          <source src={item.metadata.videoSource} />
        </Video>
      )}
      {isImageLoaded && (
        <CloseIcon
          onClick={onClose}
          iconName='close'
          trackingId={ContentDrawerTrackingIds.CLOSE_PREVIEW_MODAL}
        />
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
`;

const Image = styled.img<{ isVisible: boolean }>(
  ({ isVisible }) => `
    max-height: ${isVisible ? '80vh' : '0'};
    max-width: ${isVisible ? '80vw' : '0'};
    visibility: ${isVisible ? 'visible' : 'hidden'}
  `,
);

const Video = styled.video<{ isVisible: boolean }>(
  ({ isVisible }) => `
    max-height: ${isVisible ? '80vh' : '0'};
    max-width: ${isVisible ? '80vw' : '0'};
    visibility: ${isVisible ? 'visible' : 'hidden'}
  `,
);

const CloseIcon = styled(DefaultIconButton)(
  ({ theme }) => `
      position: absolute;
      background: white;
      top: ${theme.spacings[3]};
      right: ${theme.spacings[3]};
  `,
);
